<aura-dialog-layout [title]="dialogTitle()" [hideActions]="true">
  <div dialog-content class="w-full space-y-5">
    <!-- 1. LOADING STATE -->
    @if (isLoading()) {
      <div class="flex flex-col items-center justify-center py-16 gap-3">
        <mat-spinner diameter="36" strokeWidth="3"></mat-spinner>
        <p class="text-xs font-medium text-neutral-10 animate-pulse">Chargement du profil...</p>
      </div>
    }

    <!-- 2. ERROR STATE -->
    @else if (hasError() || !hasProfile()) {
      <div class="text-center py-12 space-y-3">
        <div
          class="size-12 rounded-2xl bg-neutral-2 dark:bg-neutral-3 text-neutral-10 flex items-center justify-center mx-auto"
        >
          <mat-icon class="text-2xl!">person_off</mat-icon>
        </div>
        <h3 class="text-base font-bold text-neutral-12">Profil introuvable</h3>
        <p class="text-xs text-neutral-10 max-w-sm mx-auto">
          Impossible de charger les informations de cet utilisateur.
        </p>
        <div class="pt-2">
          <button mat-stroked-button type="button" (click)="onClose()" class="text-xs font-semibold">
            Fermer
          </button>
        </div>
      </div>
    }

    <!-- 3. MAIN CONTENT -->
    @else {
      <!-- ==========================================
           3.A. AGENT PROFILE (MODAL-FIRST DESIGN)
           ========================================== -->
      @if (isAgent()) {
        @if (isProSectionPrivate()) {
          <div
            class="bg-white dark:bg-neutral-2 border border-neutral-3 rounded-2xl p-8 text-center space-y-3 shadow-xs my-4 max-w-md mx-auto"
          >
            <div
              class="size-12 rounded-2xl bg-neutral-3/60 text-neutral-10 flex items-center justify-center mx-auto"
            >
              <mat-icon class="text-2xl!">lock</mat-icon>
            </div>
            <h3 class="text-base font-bold text-neutral-12">Profil professionnel privé</h3>
            <p class="text-xs text-neutral-10 max-w-sm mx-auto leading-relaxed">
              Cet agent a configuré ses informations professionnelles en mode privé.
            </p>
          </div>
        } @else if (agentProfile()) {
          <div class="space-y-4">
            <!-- Vacation Mode Notice -->
            @if (isVacationMode()) {
              <div
                class="flex items-center gap-2.5 p-3 bg-amber-1 dark:bg-amber-2/30 border border-amber-4 dark:border-amber-7 rounded-xl text-amber-12 text-xs"
              >
                <mat-icon class="size-4! text-sm! text-amber-11 shrink-0">beach_access</mat-icon>
                <span
                  >Cet agent immobilier est actuellement indisponible pour de nouveaux mandats
                  immédiats.</span
                >
              </div>
            }

            <!-- Compact Agent Hero Card -->
            <div
              class="relative bg-neutral-1/60 dark:bg-neutral-3/30 border border-neutral-3 rounded-2xl p-4 sm:p-5 overflow-hidden"
            >
              <div
                class="absolute -right-8 -top-8 size-32 bg-primary-9/10 rounded-full blur-2xl pointer-events-none"
              ></div>

              <div
                class="relative z-10 flex flex-col sm:flex-row items-center sm:items-start gap-4 text-center sm:text-left"
              >
                <!-- Avatar -->
                <div class="relative shrink-0">
                  @if (effectiveAvatarUrl(); as url) {
                    <img
                      [src]="url"
                      [alt]="fullName()"
                      class="size-16 sm:size-20 rounded-2xl object-cover border-2 border-white dark:border-neutral-2 shadow-sm ring-1 ring-neutral-3"
                    />
                  } @else {
                    <div
                      class="size-16 sm:size-20 rounded-2xl bg-linear-to-br from-primary-9 via-indigo-9 to-violet-9 text-white flex items-center justify-center font-extrabold text-2xl shadow-sm border-2 border-white dark:border-neutral-2"
                    >
                      {{ initials() }}
                    </div>
                  }

                  @if (isCardActive()) {
                    <div
                      class="absolute -bottom-1 -right-1 size-6 rounded-full bg-jade-9 text-white flex items-center justify-center shadow-xs border-2 border-white dark:border-neutral-2"
                      matTooltip="Carte T certifiée vérifiée par la CCI"
                    >
                      <mat-icon class="size-3.5! text-xs! flex items-center justify-center"
                        >verified</mat-icon
                      >
                    </div>
                  }
                </div>

                <!-- Info & Badges -->
                <div class="flex-1 min-w-0 space-y-2">
                  <div>
                    <div class="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                      <h3 class="text-xl font-extrabold text-neutral-12 tracking-tight">
                        {{ fullName() }}
                      </h3>
                      @if (network()) {
                        <span
                          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-primary-2 dark:bg-primary-3/30 text-primary-11 border border-primary-4 dark:border-primary-5/40"
                        >
                          {{ network() }}
                        </span>
                      }
                    </div>

                    <div
                      class="flex flex-wrap items-center justify-center sm:justify-start gap-x-3 gap-y-1 text-xs text-neutral-10 mt-1 font-medium"
                    >
                      <span class="inline-flex items-center gap-1">
                        <mat-icon class="size-3.5! text-xs! text-primary-9">badge</mat-icon>
                        <span>{{ agentType() }}</span>
                      </span>

                      @if (activeCard()?.siren) {
                        <span>•</span>
                        <span class="font-mono text-neutral-9 dark:text-neutral-10"
                          >SIREN {{ activeCard()!.siren }}</span
                        >
                      }

                      @if (primaryRegion()) {
                        <span>•</span>
                        <span class="inline-flex items-center gap-1">
                          <mat-icon class="size-3.5! text-xs! text-neutral-9">location_on</mat-icon>
                          <span>{{ getRegionName(primaryRegion()) }}</span>
                        </span>
                      }
                    </div>
                  </div>

                  <!-- Quick trust pills -->
                  <div class="flex flex-wrap items-center justify-center sm:justify-start gap-2 pt-0.5">
                    <span
                      class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-semibold text-jade-11 bg-jade-2 dark:bg-jade-3/20 border border-jade-5 dark:border-jade-6/40"
                    >
                      <mat-icon class="size-3.5! text-xs! text-jade-9">shield</mat-icon>
                      <span>Carte professionnelle T valide (CCI)</span>
                    </span>

                    @if (averageCommissionRate()) {
                      <span
                        class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-semibold text-primary-11 bg-primary-1 dark:bg-primary-3/20 border border-primary-3 dark:border-primary-5/40"
                      >
                        <mat-icon class="size-3.5! text-xs! text-primary-9">percent</mat-icon>
                        <span>Honoraires dès {{ averageCommissionRate() | number: '1.1-2' }}%</span>
                      </span>
                    }

                    @if (openingHoursStatus(); as status) {
                      <span
                        class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-lg text-xs font-semibold border"
                        [class]="status.badgeClass"
                      >
                        <span
                          class="size-1.5 rounded-full"
                          [class.bg-primary-9]="status.isOpen"
                          [class.bg-neutral-6]="!status.isOpen"
                        ></span>
                        <span>{{ status.text }}</span>
                      </span>
                    }

                    @if (isHighlyResponsive()) {
                      <span
                        class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-semibold text-emerald-11 bg-emerald-2 dark:bg-emerald-3/20 border border-emerald-5 dark:border-emerald-6/40"
                        matTooltip="Cet agent répond rapidement et avec assiduité à l'ensemble de ses demandes"
                      >
                        <mat-icon class="size-3.5! text-xs! text-emerald-9">bolt</mat-icon>
                        <span>Agent très réactif</span>
                      </span>
                    }

                    @if (formattedResponseTime(); as respTime) {
                      <span
                        class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-semibold text-neutral-11 bg-neutral-2 dark:bg-neutral-3/30 border border-neutral-4 dark:border-neutral-5/40"
                        matTooltip="Temps de réponse moyen calculé sur ses invitations de visite et échanges"
                      >
                        <mat-icon class="size-3.5! text-xs! text-primary-9">schedule</mat-icon>
                        <span>Temps de réponse moyen : {{ respTime }}</span>
                      </span>
                    }
                  </div>
                </div>
              </div>
            </div>

            <!-- Segmented Tabs Navigation -->
            <div
              class="flex items-center p-1 bg-neutral-2 dark:bg-neutral-3/40 rounded-xl border border-neutral-3 gap-1"
            >
              <button
                type="button"
                class="flex-1 py-1.5 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                [class]="
                  activeTab() === 'services'
                    ? 'bg-white dark:bg-neutral-2 text-neutral-12 shadow-xs'
                    : 'text-neutral-10 hover:text-neutral-12'
                "
                (click)="activeTab.set('services')"
              >
                <mat-icon class="size-3.5! text-xs!">stars</mat-icon>
                <span>Présentation & services</span>
              </button>

              <button
                type="button"
                class="flex-1 py-1.5 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                [class]="
                  activeTab() === 'sector'
                    ? 'bg-white dark:bg-neutral-2 text-neutral-12 shadow-xs'
                    : 'text-neutral-10 hover:text-neutral-12'
                "
                (click)="activeTab.set('sector')"
              >
                <mat-icon class="size-3.5! text-xs!">explore</mat-icon>
                <span>Secteur & disponibilités</span>
              </button>

              <button
                type="button"
                class="flex-1 py-1.5 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer"
                [class]="
                  activeTab() === 'card'
                    ? 'bg-white dark:bg-neutral-2 text-neutral-12 shadow-xs'
                    : 'text-neutral-10 hover:text-neutral-12'
                "
                (click)="activeTab.set('card')"
              >
                <mat-icon class="size-3.5! text-xs!">verified_user</mat-icon>
                <span>Carte professionnelle</span>
              </button>
            </div>

            <!-- TAB 1: Présentation & Services -->
            @if (activeTab() === 'services') {
              <div class="space-y-4 pt-1">
                <!-- Réactivité de l'agent -->
                <div
                  class="p-4 rounded-2xl bg-neutral-1/60 dark:bg-neutral-3/30 border border-neutral-3 space-y-3"
                >
                  <div class="flex items-center justify-between">
                    <span
                      class="text-xs font-bold text-neutral-9 dark:text-neutral-10 uppercase tracking-wider flex items-center gap-1.5"
                    >
                      <mat-icon class="size-3.5! text-xs! text-primary-9">speed</mat-icon>
                      Réactivité de l'agent
                    </span>

                    @if (isHighlyResponsive()) {
                      <span
                        class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold text-emerald-11 bg-emerald-2 dark:bg-emerald-3/30 border border-emerald-5"
                      >
                        <mat-icon class="size-3.5! text-xs! text-emerald-9">bolt</mat-icon>
                        <span>Agent très réactif</span>
                      </span>
                    }
                  </div>

                  <div class="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                    <div
                      class="bg-white dark:bg-neutral-2 p-3 rounded-xl border border-neutral-3/80 shadow-2xs"
                    >
                      <span class="text-[11px] font-medium text-neutral-10 block"
                        >Temps de réponse moyen</span
                      >
                      <span class="text-sm font-bold text-neutral-12 mt-0.5 block">
                        {{ formattedResponseTime() || '< 1 h' }}
                      </span>
                    </div>

                    <div
                      class="bg-white dark:bg-neutral-2 p-3 rounded-xl border border-neutral-3/80 shadow-2xs"
                    >
                      <span class="text-[11px] font-medium text-neutral-10 block">Taux de réponse</span>
                      <span class="text-sm font-bold text-neutral-12 mt-0.5 block">
                        {{ responseRatePercent() !== null ? responseRatePercent() + ' %' : '100 %' }}
                      </span>
                    </div>

                    <div
                      class="bg-white dark:bg-neutral-2 p-3 rounded-xl border border-neutral-3/80 shadow-2xs col-span-2 sm:col-span-1"
                    >
                      <span class="text-[11px] font-medium text-neutral-10 block">Délai contractuel</span>
                      <span class="text-sm font-bold text-neutral-12 mt-0.5 block"> 24 h max </span>
                    </div>
                  </div>

                  <p class="text-xs text-neutral-10 leading-relaxed m-0">
                    Les délais de réponse sont mesurés en continu sur les demandes de visite et propositions.
                    Les expirations dégradent ce score afin de garantir des échanges dynamiques.
                  </p>
                </div>

                @if (bio()) {
                  <div
                    class="p-4 rounded-2xl bg-neutral-1/60 dark:bg-neutral-3/30 border border-neutral-3 space-y-1.5"
                  >
                    <span
                      class="text-xs font-bold text-neutral-9 dark:text-neutral-10 uppercase tracking-wider flex items-center gap-1.5"
                    >
                      <mat-icon class="size-3.5! text-xs! text-primary-9">format_quote</mat-icon>
                      Présentation et philosophie
                    </span>
                    <p class="text-sm text-neutral-11 leading-relaxed whitespace-pre-line">{{ bio() }}</p>
                  </div>
                }

                <!-- Spécialités -->
                <div class="space-y-2">
                  <span
                    class="text-xs font-bold text-neutral-9 dark:text-neutral-10 uppercase tracking-wider flex items-center gap-1.5"
                  >
                    <mat-icon class="size-3.5! text-xs! text-primary-9">category</mat-icon>
                    Spécialités de transactions
                  </span>

                  @if (specialties().length > 0) {
                    <div class="flex flex-wrap gap-2">
                      @for (spec of specialties(); track spec) {
                        @let info = getSpecialtyInfo(spec);
                        <div
                          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-neutral-1 dark:bg-neutral-3/50 text-neutral-12 border border-neutral-3"
                        >
                          <mat-icon class="size-3.5! text-xs! text-primary-9">{{ info.icon }}</mat-icon>
                          <span>{{ info.label }}</span>
                        </div>
                      }
                    </div>
                  } @else {
                    <p class="text-xs text-neutral-10 italic">Toutes transactions résidentielles.</p>
                  }
                </div>

                <!-- Prestations incluses -->
                <div class="space-y-2">
                  <span
                    class="text-xs font-bold text-neutral-9 dark:text-neutral-10 uppercase tracking-wider flex items-center gap-1.5"
                  >
                    <mat-icon class="size-3.5! text-xs! text-primary-9">check_circle</mat-icon>
                    Prestations incluses dans le mandat
                  </span>

                  @if (services().length > 0) {
                    <div class="flex flex-wrap gap-2">
                      @for (srv of services(); track srv) {
                        @let info = getServiceInfo(srv);
                        <div
                          class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-neutral-1 dark:bg-neutral-3/50 text-neutral-12 border border-neutral-3"
                        >
                          <mat-icon class="size-3.5! text-xs! text-primary-9">{{ info.icon }}</mat-icon>
                          <span>{{ info.label }}</span>
                        </div>
                      }
                    </div>
                  } @else {
                    <p class="text-xs text-neutral-10 italic">
                      Prestations personnalisées selon le projet de vente.
                    </p>
                  }
                </div>
              </div>
            }

            <!-- TAB 2: Secteur & Disponibilités -->
            @if (activeTab() === 'sector') {
              <div class="space-y-4 pt-1">
                <!-- Secteur géographique -->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <!-- Région & Départements -->
                  <div
                    class="p-3.5 rounded-2xl bg-neutral-1/60 dark:bg-neutral-3/30 border border-neutral-3 space-y-2"
                  >
                    <span
                      class="text-xs font-bold text-neutral-9 dark:text-neutral-10 uppercase tracking-wider block"
                    >
                      Région principale
                    </span>
                    <div class="text-sm font-extrabold text-neutral-12">
                      {{ getRegionName(primaryRegion()) || 'France entière' }}
                    </div>

                    @if (interventionZones().length > 0) {
                      @let depts = interventionZones()[0].departments;
                      @if (depts && depts.length > 0) {
                        <div class="pt-1 flex flex-wrap gap-1.5">
                          @for (d of depts; track d) {
                            <span
                              class="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-white dark:bg-neutral-2 text-neutral-11 border border-neutral-3"
                            >
                              {{ getDepartmentLabel(d) }}
                            </span>
                          }
                        </div>
                      }
                    }
                  </div>

                  <!-- Communes cibles -->
                  <div
                    class="p-3.5 rounded-2xl bg-neutral-1/60 dark:bg-neutral-3/30 border border-neutral-3 space-y-2"
                  >
                    <span
                      class="text-xs font-bold text-neutral-9 dark:text-neutral-10 uppercase tracking-wider block"
                    >
                      Communes prioritaires suivies
                    </span>
                    @if (preferredCities().length > 0) {
                      <div class="flex flex-wrap gap-1.5 pt-0.5">
                        @for (city of preferredCities(); track city.name + '-' + city.zipCode) {
                          <span
                            class="inline-flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs font-bold bg-white dark:bg-neutral-2 text-neutral-12 border border-neutral-3 shadow-2xs"
                          >
                            <mat-icon class="size-3! text-xs! text-primary-9">place</mat-icon>
                            <span>{{ city.name }} ({{ city.zipCode }})</span>
                          </span>
                        }
                      </div>
                    } @else {
                      <p class="text-xs text-neutral-10 italic pt-1">
                        Intervention sur l'ensemble de la région et départements limitrophes.
                      </p>
                    }
                  </div>
                </div>

                <!-- Horaires -->
                @if (hasOpeningHours()) {
                  <div
                    class="p-3.5 rounded-2xl bg-neutral-1/60 dark:bg-neutral-3/30 border border-neutral-3 space-y-2"
                  >
                    <div class="flex items-center justify-between">
                      <span
                        class="text-xs font-bold text-neutral-9 dark:text-neutral-10 uppercase tracking-wider flex items-center gap-1.5"
                      >
                        <mat-icon class="size-3.5! text-xs! text-primary-9">schedule</mat-icon>
                        Horaires de disponibilité
                      </span>

                      @if (openingHoursStatus(); as status) {
                        <span
                          class="text-xs font-bold px-2 py-0.5 rounded-full border inline-flex items-center gap-1"
                          [class]="status.badgeClass"
                        >
                          <span
                            class="size-1.5 rounded-full"
                            [class.bg-primary-9]="status.isOpen"
                            [class.bg-neutral-6]="!status.isOpen"
                          ></span>
                          {{ status.text }}
                        </span>
                      }
                    </div>

                    <aura-opening-hours-display [hours]="openingHours() ?? undefined" [showTitle]="false" />
                  </div>
                }

                <!-- Contact & Messagerie Note -->
                <div
                  class="p-3.5 rounded-2xl bg-neutral-1/60 dark:bg-neutral-3/30 border border-neutral-3 flex items-start gap-3"
                >
                  <mat-icon class="size-4! text-base! text-neutral-9 shrink-0 mt-0.5">forum</mat-icon>
                  <p class="text-xs text-neutral-10 leading-relaxed">
                    Les coordonnées directes sont protégées pendant la négociation. Utilisez la messagerie
                    ETXE pour échanger et convenir d'un rendez-vous avec cet agent.
                  </p>
                </div>
              </div>
            }

            <!-- TAB 3: Carte professionnelle T -->
            @if (activeTab() === 'card') {
              <div class="space-y-4 pt-1">
                @if (activeCard(); as card) {
                  <!-- Guarantee Title Banner -->
                  <div class="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <h4 class="text-sm font-bold text-neutral-12 flex items-center gap-1.5">
                        <mat-icon class="size-4! text-base! text-primary-9">verified_user</mat-icon>
                        <span>Garantie légale de la carte professionnelle T</span>
                      </h4>
                      <p class="text-xs text-neutral-10">
                        Délivrée par la Chambre de Commerce et d'Industrie (Loi Hoguet).
                      </p>
                    </div>

                    <span
                      class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-jade-3 dark:bg-jade-3/30 text-jade-11 border border-jade-5 dark:border-jade-6/40"
                    >
                      <mat-icon class="size-3.5! text-xs! text-jade-9">check_circle</mat-icon>
                      <span>Carte active et vérifiée</span>
                    </span>
                  </div>

                  <!-- 3D Card Showcase (Centered, prominent, interactive) -->
                  <div class="w-full max-w-[420px] mx-auto py-1">
                    <etxe-agent-card-display [card]="card" />
                  </div>

                  <!-- Legal Specifications Grid -->
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs pt-1">
                    <div
                      class="p-2.5 rounded-xl bg-neutral-1/60 dark:bg-neutral-3/30 border border-neutral-3 flex flex-col gap-0.5"
                    >
                      <span class="text-neutral-10 text-[11px] font-medium">Titulaire de la carte</span>
                      <span class="font-bold text-neutral-12 truncate" [title]="card.companyName">{{
                        card.companyName
                      }}</span>
                    </div>

                    <div
                      class="p-2.5 rounded-xl bg-neutral-1/60 dark:bg-neutral-3/30 border border-neutral-3 flex flex-col gap-0.5"
                    >
                      <span class="text-neutral-10 text-[11px] font-medium">Représentant légal</span>
                      <span class="font-bold text-neutral-12 truncate"
                        >{{ card.leader.firstName }} {{ card.leader.lastName | uppercase }}</span
                      >
                    </div>

                    <div
                      class="p-2.5 rounded-xl bg-neutral-1/60 dark:bg-neutral-3/30 border border-neutral-3 flex flex-col gap-0.5"
                    >
                      <span class="text-neutral-10 text-[11px] font-medium"
                        >Numéro de carte professionnelle</span
                      >
                      <span class="font-bold font-mono text-neutral-12 truncate">{{ card.number }}</span>
                    </div>

                    <div
                      class="p-2.5 rounded-xl bg-neutral-1/60 dark:bg-neutral-3/30 border border-neutral-3 flex flex-col gap-0.5"
                    >
                      <span class="text-neutral-10 text-[11px] font-medium">CCI émettrice</span>
                      <span class="font-bold text-neutral-12 truncate" [title]="card.cci.name">{{
                        card.cci.name
                      }}</span>
                    </div>

                    <div
                      class="p-2.5 rounded-xl bg-neutral-1/60 dark:bg-neutral-3/30 border border-neutral-3 flex flex-col gap-0.5"
                    >
                      <span class="text-neutral-10 text-[11px] font-medium">Date de fin de validité</span>
                      <span class="font-bold text-neutral-12">{{ card.expiresAt | date: 'dd/MM/yyyy' }}</span>
                    </div>

                    <div
                      class="p-2.5 rounded-xl bg-neutral-1/60 dark:bg-neutral-3/30 border border-neutral-3 flex flex-col gap-0.5"
                    >
                      <span class="text-neutral-10 text-[11px] font-medium">Cadre réglementaire</span>
                      <span class="font-bold text-jade-11"
                        >Transactions sur immeubles et fonds de commerce</span
                      >
                    </div>
                  </div>
                } @else {
                  <div class="text-center py-6 text-neutral-10 text-xs italic">
                    Aucune carte professionnelle enregistrée pour le moment.
                  </div>
                }
              </div>
            }

            <!-- Close Action Button -->
            <div class="pt-2 flex justify-end">
              <button
                mat-stroked-button
                type="button"
                class="text-xs font-semibold !px-5 !py-1.5 cursor-pointer"
                (click)="onClose()"
              >
                Fermer
              </button>
            </div>
          </div>
        }
      }

      <!-- ==========================================
           3.B. OWNER / USER MINIMAL PROFILE
           ========================================== -->
      @else {
        <div class="w-full max-w-sm mx-auto py-2 space-y-5">
          <div class="bg-white dark:bg-neutral-2 text-center space-y-4 relative overflow-hidden">
            <!-- Avatar with Initials (No personal photo to prevent bypass) -->
            <div class="relative mx-auto size-20 shrink-0">
              <div
                class="size-20 rounded-2xl bg-linear-to-br from-primary-9 via-indigo-9 to-violet-9 text-white flex items-center justify-center font-extrabold text-2xl shadow-sm border-2 border-white dark:border-neutral-2 ring-1 ring-neutral-3"
              >
                {{ initials() }}
              </div>

              <!-- Verified Badge -->
              <div
                class="absolute -bottom-1 -right-1 bg-emerald-600 text-white rounded-full p-1 shadow-xs flex items-center justify-center border-2 border-white dark:border-neutral-2"
                matTooltip="Compte vérifié"
              >
                <mat-icon class="size-3! text-[10px]!">verified</mat-icon>
              </div>
            </div>

            <!-- Identity & Role -->
            <div class="space-y-1.5">
              <h3 class="text-lg font-extrabold text-neutral-12 tracking-tight">
                {{ fullName() }}
              </h3>

              <div class="flex flex-wrap items-center justify-center gap-2">
                <span
                  class="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-xs font-bold bg-emerald-500/10 text-emerald-11 border border-emerald-500/20 shadow-2xs"
                >
                  <mat-icon class="size-3.5! text-sm!">home</mat-icon>
                  Propriétaire vendeur
                </span>
                @if (isHighlyResponsive()) {
                  <span
                    class="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700 shadow-2xs"
                  >
                    <mat-icon class="size-3.5! text-xs! text-emerald-600 dark:text-emerald-400"
                      >bolt</mat-icon
                    >
                    <span>Propriétaire très réactif</span>
                  </span>
                }
              </div>
            </div>

            <!-- Member Since -->
            @if (memberSince(); as date) {
              <div class="flex items-center justify-center gap-1.5 text-xs text-neutral-10 font-medium">
                <mat-icon class="size-3.5! text-xs! text-neutral-9">calendar_today</mat-icon>
                <span>Membre depuis {{ date | date: 'MMMM yyyy' : '' : 'fr-FR' }}</span>
              </div>
            }

            <!-- Trust & Rating Metrics (Nouveautés / Note) -->
            <div class="grid grid-cols-2 gap-2 pt-1 text-left">
              <div
                class="p-3 rounded-2xl bg-neutral-1/60 dark:bg-neutral-3/30 border border-neutral-3/70 space-y-1"
              >
                <div class="flex items-center gap-1 text-amber-500">
                  <mat-icon class="size-4! text-sm!">star</mat-icon>
                  <span class="text-xs font-bold text-neutral-12">5,0 / 5</span>
                </div>
                <p class="text-[11px] text-neutral-10 font-medium">Note vendeur</p>
              </div>

              <div
                class="p-3 rounded-2xl bg-neutral-1/60 dark:bg-neutral-3/30 border border-neutral-3/70 space-y-1"
              >
                <div class="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                  <mat-icon class="size-4! text-sm!">schedule</mat-icon>
                  <span class="text-xs font-bold text-neutral-12">{{
                    formattedResponseTime() || '< 1 h'
                  }}</span>
                </div>
                <p class="text-[11px] text-neutral-10 font-medium">Temps de réponse moyen</p>
              </div>
            </div>

            <!-- Platform Guarantees -->
            <div
              class="p-3 rounded-2xl bg-neutral-1/40 dark:bg-neutral-3/20 border border-neutral-3/60 text-left space-y-2"
            >
              <p class="text-[11px] font-bold text-neutral-11 uppercase tracking-wider">
                Garanties plateforme
              </p>
              <div class="space-y-1.5 text-xs text-neutral-11">
                <div class="flex items-center gap-2">
                  <mat-icon class="size-3.5! text-xs! text-emerald-600">check_circle</mat-icon>
                  <span>Identité vérifiée</span>
                </div>
                <div class="flex items-center gap-2">
                  <mat-icon class="size-3.5! text-xs! text-emerald-600">check_circle</mat-icon>
                  <span>Projet de vente validé</span>
                </div>
              </div>
            </div>

            <!-- Privacy Protection Notice -->
            <div
              class="p-3 rounded-2xl bg-primary-9/5 border border-primary-9/15 text-left flex items-start gap-2.5"
            >
              <mat-icon class="size-4! text-base! text-primary-10 shrink-0 mt-0.5">lock</mat-icon>
              <p class="text-[11px] text-neutral-10 leading-relaxed">
                Les coordonnées directes et le nom complet sont protégés. Ils sont transmis de façon sécurisée
                uniquement lors de l'attribution du mandat.
              </p>
            </div>

            <!-- Close Action -->
            <div class="pt-1">
              <button
                mat-flat-button
                type="button"
                class="primary !w-full !py-2 text-xs font-bold cursor-pointer"
                (click)="onClose()"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      }
    }
  </div>
</aura-dialog-layout>
